import classNames from 'classnames';
import { Popover } from 'rsuite';
import type { PopoverProps } from 'rsuite/lib/Popover';
import {
  CostNegotiationMode,
  ENTRY_COST_UNAVAILABLE_MESSAGE,
  type CostNegotiationModeValue,
} from '../../costNegotiation';
import { formatDecimal } from '../../utils';
import styles from './CostDetailsPopover.module.scss';

export type CostDetailsPopoverRow = {
  label: string;
  value: string;
  tone?: 'positive' | 'negative';
};

type CostDetailsPopoverProps = PopoverProps & {
  rows: CostDetailsPopoverRow[];
  title?: string;
};

type CurrentCostDetailsParams = {
  costNegotiationMode: CostNegotiationModeValue;
  currentCost: number;
  currentLiquidCost: number;
  currentPMZ: number;
  /** A de ENTRADA, que converte o custo de entrada em líquido. Só existe naquele modo. */
  purchaseTaxPercentage?: number | null;
  /** A de SAÍDA, que sustenta o PMZ. Existe nos dois modos. */
  salesTaxPercentage?: number | null;
  // Sinal explicito de indisponibilidade do dado de origem congelado no fechamento -- quando
  // verdadeiro, nenhum dos quatro numeros do detalhamento normal pode ter sido calculado a partir dele.
  isEntryCostUnavailable?: boolean;
};

/** Alíquota e variação não entram na dica do NOVO custo; a do CUSTO ATUAL traz as duas alíquotas. */
type NewCostDetailsParams = {
  costNegotiationMode: CostNegotiationModeValue;
  newCost: number;
  newLiquidCost: number;
  PMZNewCost: number;
  isEntryCostUnavailable?: boolean;
  sellinAllowance?: number | null;
  /** O custo líquido com a verba abatida, derivado pelo MOTOR. */
  effectiveNetCost?: number | null;
  sellinAllowanceToSupplier?: number | null;
};

const ALIQUOTA_DE_ENTRADA = 'Alíquota de entrada';
const ALIQUOTA_DE_SAIDA = 'Alíquota de saída';

export const buildCurrentCostDetailsRows = ({
  costNegotiationMode,
  currentCost,
  currentLiquidCost,
  currentPMZ,
  purchaseTaxPercentage,
  salesTaxPercentage,
  isEntryCostUnavailable,
}: CurrentCostDetailsParams): CostDetailsPopoverRow[] => {
  if (costNegotiationMode === CostNegotiationMode.ENTRY_COST && isEntryCostUnavailable) {
    // Substitui as quatro linhas normais por exatamente uma, para que nenhum detalhamento meio
    // calculado apareca -- nenhum dos quatro numeros abaixo pode ter sido gravado no fechamento.
    return [{ label: ENTRY_COST_UNAVAILABLE_MESSAGE, value: '' }];
  }

  // A de SAÍDA vem nos dois modos, e imediatamente antes do PMZ, que sai dela.
  const aliquotaDeSaidaRow: CostDetailsPopoverRow = { label: ALIQUOTA_DE_SAIDA, value: `${formatDecimal(salesTaxPercentage ?? 0, 1)}%` };
  const pmzRow: CostDetailsPopoverRow = { label: 'PMZ', value: `R$ ${formatDecimal(currentPMZ, 2)}` };
  const custoLiquidoRow: CostDetailsPopoverRow = { label: 'Custo líquido', value: `R$ ${formatDecimal(currentLiquidCost, 2)}` };

  if (costNegotiationMode === CostNegotiationMode.ENTRY_COST) {
    return [
      { label: 'Custo de entrada', value: `R$ ${formatDecimal(currentCost, 2)}` },
      { label: ALIQUOTA_DE_ENTRADA, value: `${formatDecimal(purchaseTaxPercentage ?? 0, 1)}%` },
      custoLiquidoRow,
      aliquotaDeSaidaRow,
      pmzRow,
    ];
  }

  return [custoLiquidoRow, aliquotaDeSaidaRow, pmzRow];
};

const temNumero = (value?: number | null): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * **A verba é subtraída do custo LÍQUIDO, não do de entrada**, e por isso "Custo com verba" vem
 * depois de "Custo líquido" e a subtração vem depois da conversão.
 */
const buildSellinAllowanceRows = ({
  sellinAllowance,
  effectiveNetCost,
  sellinAllowanceToSupplier,
  costNegotiationMode,
}: Pick<NewCostDetailsParams, 'sellinAllowance' | 'effectiveNetCost' | 'sellinAllowanceToSupplier' | 'costNegotiationMode'>): {
  antesDoPMZ: CostDetailsPopoverRow[];
  depoisDoPMZ: CostDetailsPopoverRow[];
} => {
  // `undefined` é linha sem o contrato da verba; `null` ou zero é contrato sem acordo, e aí a
  // cadeia aparece ZERADA. Os zeros são literais: `sellinAllowanceToSupplier` recua para a
  // SUGESTÃO quando ninguém acordou nada.
  const semContratoDaVerba = sellinAllowance === undefined;
  if (semContratoDaVerba) {
    return { antesDoPMZ: [], depoisDoPMZ: [] };
  }

  if (!temNumero(sellinAllowance) || sellinAllowance <= 0) {
    return {
      antesDoPMZ: [{ label: 'Verba de sell-in', value: '- R$ 0,00' }],
      depoisDoPMZ:
        costNegotiationMode === CostNegotiationMode.ENTRY_COST ? [{ label: 'Verba ao fornecedor', value: 'R$ 0,00' }] : [],
    };
  }

  if (!temNumero(effectiveNetCost)) {
    return { antesDoPMZ: [], depoisDoPMZ: [] };
  }

  const antesDoPMZ: CostDetailsPopoverRow[] = [
    { label: 'Verba de sell-in', value: `- R$ ${formatDecimal(sellinAllowance, 2)}` },
    { label: 'Custo com verba', value: `R$ ${formatDecimal(effectiveNetCost, 2)}` },
  ];

  // A conversão ao fornecedor só significa algo onde existe custo de entrada.
  const mostrarAConversao = costNegotiationMode === CostNegotiationMode.ENTRY_COST && temNumero(sellinAllowanceToSupplier);

  return {
    antesDoPMZ,
    depoisDoPMZ: mostrarAConversao ? [{ label: 'Verba ao fornecedor', value: `R$ ${formatDecimal(sellinAllowanceToSupplier, 2)}` }] : [],
  };
};

export const buildNewCostDetailsRows = ({
  costNegotiationMode,
  newCost,
  newLiquidCost,
  PMZNewCost,
  isEntryCostUnavailable,
  sellinAllowance,
  effectiveNetCost,
  sellinAllowanceToSupplier,
}: NewCostDetailsParams): CostDetailsPopoverRow[] => {
  const novoCustoRow: CostDetailsPopoverRow = { label: 'Novo custo', value: `R$ ${formatDecimal(newCost, 2)}` };
  const pmzRow: CostDetailsPopoverRow = { label: 'PMZ', value: `R$ ${formatDecimal(PMZNewCost, 2)}` };

  // Sem a alíquota congelada a verba não tem de onde ser subtraída.
  const semCadeiaParaAVerba = costNegotiationMode === CostNegotiationMode.ENTRY_COST && Boolean(isEntryCostUnavailable);

  const verba = semCadeiaParaAVerba
    ? { antesDoPMZ: [], depoisDoPMZ: [] }
    : buildSellinAllowanceRows({ sellinAllowance, effectiveNetCost, sellinAllowanceToSupplier, costNegotiationMode });

  const custoLiquidoRow: CostDetailsPopoverRow[] =
    semCadeiaParaAVerba || costNegotiationMode !== CostNegotiationMode.ENTRY_COST
      ? []
      : [{ label: 'Custo líquido', value: `R$ ${formatDecimal(newLiquidCost, 2)}` }];

  return [novoCustoRow, ...custoLiquidoRow, ...verba.antesDoPMZ, pmzRow, ...verba.depoisDoPMZ];
};

export const CostDetailsPopover = ({ rows, title = 'Detalhes', className, ...popoverProps }: CostDetailsPopoverProps) => (
  <Popover {...popoverProps} className={classNames(styles['cost-details-popover'], className)}>
    <div className={styles['cost-details-popover__content']}>
      <h4 className={styles['cost-details-popover__title']}>{title}</h4>
      {rows.map(row => (
        <div className={styles['cost-details-popover__line']} key={row.label}>
          <span className={styles['cost-details-popover__label']}>{row.label}</span>
          <span
            className={classNames(styles['cost-details-popover__value'], {
              [styles['cost-details-popover__value--negative']]: row.tone === 'negative',
              [styles['cost-details-popover__value--positive']]: row.tone === 'positive',
            })}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  </Popover>
);
